import React, { Fragment, useCallback, useMemo } from 'react';
import { format, parse } from 'date-fns';
import styled, { css } from 'styled-components';
import responsive from '@/se/utilities/responsive';
import { translateToUTC } from '@/util/dateTime';
import { isSameMonth } from 'date-fns';
import { withTheme } from '@/se/theme';
import Slot from './Slot';
import { HeaderCell } from './Cell';

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(1, max-content) repeat(5, 1fr);
  grid-template-rows: repeat(${props => props.numberOfRows}, min-content);
  grid-column-gap: 0.1666666667em;
  grid-auto-flow: columns;
  margin-bottom: 0.33333em;
`;

const Slots = styled.div`
  display: grid;
  grid-column: 2 / span 6;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(1, 1fr);
  grid-column-gap: 0.1666666667em;
  margin-bottom: 0.08333333333em;
`;

const Row = styled(Slots)`
  grid-template-rows: auto;
  margin-bottom: 0;
`;

const OrCell = styled(HeaderCell)`
  ${responsive.md.andSmaller`
    writing-mode: vertical-rl;
    text-orientation: mixed;
  `}
`;

const Day = styled.div`
  font-size: 1.25em;
  color: ${withTheme(theme => theme.textColor.string())};
`;

const TimelineWrapper = ({
  date,
  bookings = {},
  dateSelection,
  physicianSelection,
  room,
  onCellClick,
  firstWeek,
  colorMode,
  dayStartTime = '05:00',
  dayEndTime = '17:00',
}) => {
  const hourMarkers = useMemo(() => getHourMarkers(dayStartTime, dayEndTime), [dayEndTime, dayStartTime]);

  const bookingsForRoom = useMemo(
    () =>
      (bookings[`${room.id}_${format(date, 'YYYY-MM-DD')}`] ?? []).map(booking => ({
        ...booking,
        start: translateToUTC(parse(booking.start)),
        end: translateToUTC(parse(booking.end)),
      })),
    [bookings, date, room.id]
  );

  const isGrayed = useMemo(() => !!isSameMonth(date, dateSelection), [date, dateSelection]);

  const onSlotClick = useCallback(
    value => {
      const booker =
        value.booking && value.booking.physician
          ? `physician-${value.booking.physician.id}`
          : value.booking && value.booking.organization
            ? `partner-${value.booking.organization.id}`
            : 'open';
      onCellClick({ room, date, booker, ...value });
    },
    [date, onCellClick, room]
  );

  return (
    <Timeline makeRoom={firstWeek}>
      {hourMarkers.map(({ hour, position }) => (
        <VerticalTick key={`hour-${hour}`} position={position}>
          {firstWeek && <label>{hour}</label>}
        </VerticalTick>
      ))}
      <Slot
        colorMode={colorMode}
        grayed={isGrayed}
        physicianSelection={physicianSelection}
        key={date}
        date={date}
        bookings={bookingsForRoom}
        onClick={onSlotClick}
        dayStartTime={dayStartTime}
        dayEndTime={dayEndTime}
      />
    </Timeline>
  );
};

const Timeline = styled.div`
  background: rgba(255, 255, 255, 0.08);
  min-height: 4em;
  position: relative;
  ${props =>
    props.makeRoom &&
    css`
      padding-top: 1.5em;
      min-height: 5.5em;
    `}
`;

const DateHeader = ({ dates = [] }) => (
  <Row>
    {dates.map((day, i) => (
      <Fragment key={`${day}-${i}`}>
        <HeaderCell>
          <Day>{format(day, 'D')}</Day>
        </HeaderCell>
      </Fragment>
    ))}
  </Row>
);

const VerticalTick = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0.08rem;
  background-color: rgba(255, 255, 255, 0.12);
  left: ${props => props.position}%;

  > label {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0.6;
    white-space: nowrap;
  }
`;

const bottomMargin = { marginBottom: '0.1666666667em' };
const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const orCellStyle = { gridColumn: 1 };

const Week = ({
  dates = [],
  bookings = {},
  dateSelection,
  physicianSelection,
  rooms,
  onCellClick,
  showDayNames,
  firstWeek,
  colorMode,
  dayStartTime = '05:00',
  dayEndTime = '17:00',
}) => (
  <Container numberOfRows={showDayNames ? 3 : 2}>
    {showDayNames && (
      <Fragment>
        <HeaderCell style={bottomMargin} />
        <Row>
          {dayNames.map((day, i) => (
            <HeaderCell key={`${day}-${i}`} style={bottomMargin}>
              {day}
            </HeaderCell>
          ))}
        </Row>
      </Fragment>
    )}
    <HeaderCell />

    <DateHeader dates={dates} />

    {rooms.map((room, i) => (
      <Fragment key={`${room.id}-${i}`}>
        <OrCell style={orCellStyle}>{room.name}</OrCell>
        <Slots>
          {dates.map((date, k) => (
            <TimelineWrapper
              key={`${date}-${k}`}
              date={date}
              bookings={bookings}
              dateSelection={dateSelection}
              physicianSelection={physicianSelection}
              room={room}
              onCellClick={onCellClick}
              showDayNames={showDayNames}
              firstWeek={firstWeek && i === 0}
              colorMode={colorMode}
              dayStartTime={dayStartTime}
              dayEndTime={dayEndTime}
            />
          ))}
        </Slots>
      </Fragment>
    ))}
  </Container>
);

function getHourMarkers(dayStartTime, dayEndTime) {
  const safeStartTime = dayStartTime || '05:00';
  const safeEndTime = dayEndTime || '17:00';

  const [startHour, startMinute] = safeStartTime.split(':').map(Number);
  const [endHour, endMinute] = safeEndTime.split(':').map(Number);

  const dayStartMinutes = startHour * 60 + startMinute;
  const dayEndMinutes = endHour * 60 + endMinute;
  const totalDayMinutes = dayEndMinutes - dayStartMinutes;

  const markers = [];

  for (let h = startHour + 2; h < endHour; h += 2) {
    const hourMinutes = h * 60;
    const position = ((hourMinutes - dayStartMinutes) / totalDayMinutes) * 100;
    markers.push({ hour: h, position });
  }

  return markers;
}

export default Week;
